//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  TikTokMusicGenre,
  type TikTokMusicGenre as TikTokMusicGenreType,
} from '@apis/tiktok-trends/tiktokMusicGenre';
import {
  TikTokPopularCommercialTrack,
  TikTokPopularCommercialTrackClip,
} from '@apis/tiktok-trends/tiktokTrendsModel';
import { TikTokMusicSoundInfoConfig } from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import { isDefined } from '@utils/filterPredicates';
import { getMsToSeconds, getSecondsToMs } from '@utils/timingUtils';

export const musicSoundInfoMsToSeconds = (
  info: TikTokMusicSoundInfoConfig | undefined,
): TikTokMusicSoundInfoConfig | undefined => {
  if (!isDefined(info)) {
    return undefined;
  }
  return {
    ...info,
    start: isDefined(info.start) ? getMsToSeconds(info.start) : undefined,
    end: isDefined(info.end) ? getMsToSeconds(info.end) : undefined,
  };
};

export const musicSoundInfoSecondsToMs = (
  info: TikTokMusicSoundInfoConfig | undefined,
): TikTokMusicSoundInfoConfig | undefined => {
  if (!isDefined(info)) {
    return undefined;
  }
  return {
    ...info,
    start: isDefined(info.start) ? getSecondsToMs(info.start) : undefined,
    end: isDefined(info.end) ? getSecondsToMs(info.end) : undefined,
  };
};

/**
 * Converts a TikTok music genre enum value into a human-readable label.
 * Replaces underscores with spaces and normalizes each word to title case
 * by uppercasing the first letter after a non-letter boundary and
 * lowercasing subsequent letters.
 */
export const getTikTokGenreLabel = (genre: TikTokMusicGenreType): string => {
  if (genre === TikTokMusicGenre.All) {
    return 'All Genres';
  }

  return genre
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

export const resolveTrackInfo = (
  track: TikTokPopularCommercialTrack,
): TikTokPopularCommercialTrackClip => {
  // Select full duration clip if available, otherwise fallback to trending clip.
  const resolved = [track.fullDurationSongClip, track.trendingSongClip]
    .filter(isDefined)
    .filter(
      (clip) =>
        isDefined(clip.duration) &&
        isDefined(clip.songClipId) &&
        isDefined(clip.previewUrl),
    )
    .map((clip) => ({
      duration: clip.duration,
      songClipId: clip.songClipId,
      previewUrl: clip.previewUrl,
    }));

  return (
    resolved[0] ?? {
      duration: undefined,
      songClipId: undefined,
      previewUrl: undefined,
    }
  );
};

export const clampTrimStart = (
  rawStart: number,
  currentEnd: number,
  trackDuration: number,
): { end: number; start: number } => {
  const start = Math.max(0, Math.min(rawStart, trackDuration - 1));
  const end = Math.max(currentEnd, start + 1);
  return { end, start };
};

export const clampTrimEnd = (
  rawEnd: number,
  currentStart: number,
  trackDuration: number,
  videoDurationSeconds: number | undefined,
): { end: number; start: number } => {
  const nextStart = Math.max(0, Math.min(currentStart, rawEnd - 1));
  const maxEnd = isDefined(videoDurationSeconds)
    ? Math.min(trackDuration, nextStart + videoDurationSeconds)
    : trackDuration;
  const end = Math.max(1, Math.max(nextStart + 1, Math.min(rawEnd, maxEnd)));
  return { end, start: nextStart };
};

export const clampTrimRange = (
  newStart: number,
  rawEnd: number,
  videoDurationSeconds: number | undefined,
): { end: number; start: number } => {
  const cappedEnd = isDefined(videoDurationSeconds)
    ? Math.min(rawEnd, newStart + videoDurationSeconds)
    : rawEnd;
  const end = Math.max(newStart + 1, cappedEnd);
  return { end, start: newStart };
};

/**
 * Parses a time string in "M:SS" format (e.g. "1:30" → 90) or plain integer
 * seconds (e.g. "90" → 90). Returns undefined on invalid input.
 */
export const parseTimeInput = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (/^\d+:\d{1,2}$/.test(trimmed)) {
    const [minutesPart, secondsPart] = trimmed.split(':');
    const minutes = parseInt(minutesPart ?? '0', 10);
    const seconds = parseInt(secondsPart ?? '0', 10);
    return seconds < 60 ? minutes * 60 + seconds : undefined;
  }
  if (/^\d+$/.test(trimmed)) {
    return parseInt(trimmed, 10);
  }
  return undefined;
};
